/**
 * AdminRolesPage — Role Management admin UI.
 *
 * Admin can Create / Edit / Delete (custom only) / Archive / Reactivate any
 * role. The four seed roles (SIA / CSO / Cleaner / Supervisor) are marked
 * with a "SEED" chip and can be renamed + re-priced but not deleted.
 *
 * Every list in the app (Upcoming Shifts Staff Requirements, Shift Calendar
 * role dropdown, Employee Assignment) reads roles from GET /api/roles via
 * the `useRoles` hook, so any change here propagates instantly.
 */
import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Archive, ArchiveRestore, Shield,
  Coins, Search, Loader2, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AdminLayout from '../../components/layout/AdminLayout';
import { useRoles, CURRENCY_OPTIONS, currencySymbol } from '../../hooks/useRoles';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FORM = {
  name: '',
  default_hourly_rate: '',
  default_currency: 'GBP',
  description: '',
  active: true,
};

export default function AdminRolesPage() {
  const { roles, loading, refresh } = useRoles();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // active | archived | all
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // role object or null
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // role or null

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return roles.filter((r) => {
      if (statusFilter === 'active' && !r.active) return false;
      if (statusFilter === 'archived' && r.active) return false;
      if (!q) return true;
      return (
        (r.name || '').toLowerCase().includes(q) ||
        (r.slug || '').toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q)
      );
    });
  }, [roles, search, statusFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setDialogOpen(true);
  };

  const openEdit = (role) => {
    setEditing(role);
    setForm({
      name: role.name || '',
      default_hourly_rate: role.default_hourly_rate ? String(role.default_hourly_rate) : '',
      default_currency: role.default_currency || 'GBP',
      description: role.description || '',
      active: !!role.active,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        default_hourly_rate: form.default_hourly_rate === '' ? 0 : Math.max(0, parseFloat(form.default_hourly_rate) || 0),
        default_currency: form.default_currency || 'GBP',
        description: form.description.trim() || null,
        active: form.active,
      };
      if (editing) {
        await axios.put(`${API}/roles/${editing.id}`, payload);
        toast.success('Role updated');
      } else {
        await axios.post(`${API}/roles`, payload);
        toast.success('Role created');
      }
      setDialogOpen(false);
      setForm(DEFAULT_FORM);
      setEditing(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (role) => {
    try {
      await axios.post(`${API}/roles/${role.id}/${role.active ? 'archive' : 'activate'}`);
      toast.success(role.active ? 'Role archived' : 'Role reactivated');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/roles/${confirmDelete.id}`);
      toast.success('Role deleted');
      setConfirmDelete(null);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete role');
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-roles-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" /> Role Management
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Roles power the Hourly Rate, Staff Requirements block and role dropdown across the app.
              Any change here reflects immediately in Upcoming Shifts, Shift Calendar and Employee Assignment.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="rounded-sm"
            data-testid="roles-add-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add New Role
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search roles..."
              className="pl-9 bg-background border-border rounded-sm"
              data-testid="roles-search"
            />
          </div>
          <div className="flex gap-1 border border-border rounded-sm p-0.5 bg-muted/30">
            {[
              { key: 'active', label: 'Active' },
              { key: 'archived', label: 'Archived' },
              { key: 'all', label: 'All' },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={cn(
                  'px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors',
                  statusFilter === f.key
                    ? 'bg-primary/20 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                data-testid={`roles-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed border-border/60 bg-muted/20">
            <CardContent className="py-16 text-center">
              <Shield className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {search ? 'No roles match your search.' : 'No roles yet — click "Add New Role" to create one.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((role) => (
              <Card
                key={role.id}
                className={cn(
                  'border-border/60 bg-card/50 hover:border-primary/40 transition-colors',
                  !role.active && 'opacity-70'
                )}
                data-testid={`role-card-${role.slug}`}
              >
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold text-foreground truncate">
                          {role.name}
                        </h3>
                        {role.is_seed && (
                          <Badge variant="outline" className="text-[9px] uppercase tracking-widest border-primary/40 text-primary/80 h-4 px-1.5">
                            Seed
                          </Badge>
                        )}
                        {!role.active && (
                          <Badge variant="outline" className="text-[9px] uppercase tracking-widest border-muted-foreground/40 text-muted-foreground h-4 px-1.5">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground/60 mt-0.5 font-mono">
                        {role.slug}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Coins className="w-4 h-4" />
                    <span className="text-foreground font-medium tabular-nums">
                      {currencySymbol(role.default_currency)}{Number(role.default_hourly_rate).toFixed(2)}
                    </span>
                    <span className="text-xs">/hr default</span>
                  </div>

                  {role.description && (
                    <p className="text-xs text-muted-foreground/80 line-clamp-2">
                      {role.description}
                    </p>
                  )}

                  <div className="flex items-center gap-1.5 pt-2 border-t border-border/40">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 rounded-sm text-xs"
                      onClick={() => openEdit(role)}
                      data-testid={`role-edit-${role.slug}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 rounded-sm text-xs"
                      onClick={() => handleArchive(role)}
                      data-testid={`role-toggle-active-${role.slug}`}
                    >
                      {role.active ? (
                        <><Archive className="w-3.5 h-3.5 mr-1" /> Archive</>
                      ) : (
                        <><ArchiveRestore className="w-3.5 h-3.5 mr-1" /> Reactivate</>
                      )}
                    </Button>
                    {!role.is_seed && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-sm text-xs text-red-400 hover:text-red-300 ml-auto"
                        onClick={() => setConfirmDelete(role)}
                        data-testid={`role-delete-${role.slug}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Role' : 'Add New Role'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Renaming a seed role keeps its slug — payroll history stays intact.'
                : 'This role appears in every dropdown across the app after saving.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role-name" className="text-xs uppercase tracking-wider text-muted-foreground">
                Role / Position Name *
              </Label>
              <Input
                id="role-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Site Manager"
                className="bg-background border-border rounded-sm"
                data-testid="role-form-name"
                required
              />
            </div>

            <div className="grid grid-cols-[1fr_120px] gap-2">
              <div className="space-y-2">
                <Label htmlFor="role-rate" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Default Hourly Rate
                </Label>
                <Input
                  id="role-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.default_hourly_rate}
                  onChange={(e) => setForm({ ...form, default_hourly_rate: e.target.value })}
                  placeholder="0.00"
                  className="bg-background border-border rounded-sm"
                  data-testid="role-form-rate"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Currency
                </Label>
                <Select
                  value={form.default_currency}
                  onValueChange={(val) => setForm({ ...form, default_currency: val })}
                >
                  <SelectTrigger
                    className="bg-background border-border rounded-sm"
                    data-testid="role-form-currency"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CURRENCY_OPTIONS.map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="role-desc" className="text-xs uppercase tracking-wider text-muted-foreground">
                Description (Optional)
              </Label>
              <textarea
                id="role-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short description of what this role does..."
                rows={3}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm resize-none"
                data-testid="role-form-description"
              />
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 border border-border rounded-sm bg-muted/30">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="accent-primary"
                data-testid="role-form-active"
              />
              <span className="flex-1">
                <span className="font-medium">Active</span>
                <span className="block text-[11px] text-muted-foreground">
                  Uncheck to archive — role stays visible on historical events but hidden from new-shift dropdowns.
                </span>
              </span>
            </label>

            {editing?.is_seed && (
              <div className="flex items-start gap-2 p-2 rounded-sm bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                This is a seed role. It cannot be deleted (only archived) so payroll history keeps its label.
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                data-testid="role-form-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !form.name.trim()}
                data-testid="role-form-save"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editing ? 'Save Changes' : 'Create Role'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="!bg-card border-border" data-testid="role-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete role “{confirmDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              Historical events / shifts that used this role keep their payroll data — the label just falls back to the slug on those records. New shifts can no longer pick this role.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="role-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="role-delete-confirm"
            >
              Delete role
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
